import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { useClientSessionHistory } from '@/hooks/sessions/useClientSessionHistory';
import { useRevertSessionConsumption } from '@/hooks/sessions/useRevertSessionConsumption';
import { CheckCircle, XCircle, Clock, Calendar, Plus, RotateCcw, RefreshCcw } from 'lucide-react';
import { formatPaymentDate, formatLocalizedDate } from '@/utils/dateHelpers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SheetDescription } from '@/components/ui/sheet';
interface SessionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
}
const SessionHistoryDialog: React.FC<SessionHistoryDialogProps> = ({
  open,
  onOpenChange,
  clientId,
  clientName
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const {
    data: sessionHistory = [],
    isLoading,
    error
  } = useClientSessionHistory({
    clientId,
    limit: 1000 // Retrieve all sessions
  });
  
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const revertMutation = useRevertSessionConsumption();

  const handleRevertClick = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setRevertDialogOpen(true);
  };

  const handleRevertConfirm = () => {
    if (selectedSessionId) {
      revertMutation.mutate({ consumptionId: selectedSessionId });
    }
    setRevertDialogOpen(false);
    setSelectedSessionId(null);
  };
  const getConsumptionIcon = (type: 'completed' | 'missed') => {
    if (type === 'completed') {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
    return <XCircle className="h-4 w-4 text-red-600" />;
  };
  const getConsumptionBadge = (type: 'completed' | 'missed') => {
    if (type === 'completed') {
      return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
          {t('sessions.completed')}
        </Badge>;
    }
    return <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">
        {t('sessions.missed')}
      </Badge>;
  };
  const content = <div className="space-y-4">
      {isLoading && <div className="text-center text-muted-foreground py-8">
          <Clock className="h-8 w-8 mx-auto mb-2 animate-spin" />
          {t('common.loading')}
        </div>}

      {error && <div className="text-center text-destructive py-8">
          {t('sessions.historyLoadFailed')}
        </div>}

      {!isLoading && !error && sessionHistory.length === 0 && <div className="text-center text-muted-foreground py-8">
          {t('sessions.noSessionHistory')}
        </div>}

      {!isLoading && !error && sessionHistory.length > 0 && <div className="space-y-3 max-h-96">
          {sessionHistory.map((session, index) => <Card 
              key={session.consumption_id || `recurring-${session.consumption_date}-${index}`}
              className={session.is_cycle_start ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800" : ""}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getConsumptionIcon(session.consumption_type)}
                      <div className="font-medium">
                        {session.workout_type_name}
                      </div>
                      {getConsumptionBadge(session.consumption_type)}
                      {session.is_cycle_start && (
                        <Badge variant="outline" className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                          <RefreshCcw className="h-3 w-3 mr-1" />
                          {t('sessions.newCycle')}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        <strong>{t('common.date')}:</strong>{' '}
                        {formatPaymentDate(session.booking_date, i18n.language)}
                      </div>
                      <div>
                        <strong>{t('common.time')}:</strong>{' '}
                        {session.time_slot}
                      </div>
                      <div className="flex items-center gap-2">
                        <Plus className="h-3 w-3" />
                        <strong>{t('common.createdAt')}:</strong>{' '}
                        {formatPaymentDate(session.created_at, i18n.language)}
                      </div>
                      {session.notes && <div>
                          <strong>{t('common.notes')}:</strong>{' '}
                          {session.notes}
                        </div>}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevertClick(session.consumption_id)}
                      disabled={revertMutation.isPending}
                      className="h-8 px-2"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>}
    </div>;
  return <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-screen w-full overflow-y-auto rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix">
        <SheetHeader className="p-6 border-b flex-shrink-0">
          <SheetTitle className="greek-text">
            {t('sessions.sessionHistory')} - {clientName}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 p-6 pb-safe-bottom">
          {content}
        </div>
      </SheetContent>
      
      {isMobile || isTablet ? (
        <Sheet open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
          <SheetContent 
            side="bottom" 
            className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-[300px] rounded-t-lg'}`}
          >
            <SheetHeader className={`text-left ${isTablet ? 'p-6 border-b flex-shrink-0' : ''}`}>
              <SheetTitle>{t('sessions.confirmRevert')}</SheetTitle>
              <SheetDescription>
                {t('sessions.confirmRevertDescription')}
              </SheetDescription>
            </SheetHeader>
            <div className={`flex flex-col gap-3 ${isTablet ? 'flex-1 p-6 pb-safe-bottom justify-center' : 'mt-6'}`}>
              <Button onClick={handleRevertConfirm} className="w-full">
                {t('sessions.revert')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setRevertDialogOpen(false)}
                className="w-full"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <AlertDialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('sessions.confirmRevert')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('sessions.confirmRevertDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleRevertConfirm}>
                {t('sessions.revert')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Sheet>;
};
export default SessionHistoryDialog;